import { inventories } from "@/api/inventories/useInventoriesQuery";
import { purchaseOrderItems } from "@/api/purchaseOrderItems/usePurchaseOrderItems";
import { mutateStockPlans } from "@/api/stockPlan/useStockPlanQuery";
import { useModal } from "@/features/modal/ModalStackManager";
import { FormButtonBox } from "@/features/standard/form/FormButtonBox";
import { FormMain } from "@/features/standard/form/FormMain";
import { FormWrapper } from "@/features/standard/form/FormWrapper";
import customAlert from "@/features/ui/alert/Alert";
import { ItemAutoComplete } from "@/features/ui/autoComplete/item/item-autoComplete";
import { LocationAutoComplete } from "@/features/ui/autoComplete/location/location-autoComplate";
import { PartnerAutoComplete } from "@/features/ui/autoComplete/partner/partner-autoComplete";
import { PurchaseOrderItemsAutoComplete } from "@/features/ui/autoComplete/purchaseOrderItems/purchaseOrderItems-autoComplete";
import { UserAutoComplete } from "@/features/ui/autoComplete/user/user-autoComplete";
import { handleErrorResponse } from "@/utils/errorMessage";
import timeUtil from "@/utils/timeUtil";
import { validateFormValues } from "@/utils/validation";
import { Button, Checkbox, Flex, NumberInput, Select, Text, TextInput } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import {
  StockPlansGet200ResponseRowsInnerDirectionEnum,
  StockPlansGetRequestDirectionEnum,
  WmsApiStockPlansPostRequest,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconCalendar } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

const Create = Object.assign(FormMain, {
  Button: Button,
  Input: TextInput,
  Wrapper: FormWrapper,
  ButtonBox: FormButtonBox,
  Text: Text,
  Flex: Flex,
  DateInput: DateInput,
});

interface StockPlanCreateFormProps {
  code?: string;
  partnerCode?: string;
  autoCompleteOnSubmit?: (values: any) => void;
}
export const StockPlanCreateForm = (params: StockPlanCreateFormProps) => {
  const { code, partnerCode, autoCompleteOnSubmit } = params;
  const { closeModal } = useModal();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(false);
  const { mutate: createMutate } = useMutation(
    (params: WmsApiStockPlansPostRequest) =>
      mutateStockPlans.create(params).mutationFn(params as WmsApiStockPlansPostRequest | any),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["stockPlans"]);
        closeModal(form.values);
        customAlert(
          t("message.createSuccess", { item: t("stockPlan.stockPlan") }),
          t("common.createSuccess"),
          "green",
        );
      },
      onError: (error: any) => {
        customAlert(
          handleErrorResponse({ error, errorContext: t("stockPlan.stockPlan") }),
          t("common.createFail", { item: t("stockPlan.stockPlan") }),
          "red",
        );
      },
    },
  );

  const form = useForm<{
    partnerCode: string | undefined;
    code?: string;
    direction?: StockPlansGetRequestDirectionEnum | undefined;
    scheduledAt: string;
    itemCode: string;
    lotId?: number;
    quantity: string;
    userCode?: string;
    sourceLocationCode?: string;
    targetLocationCode?: string;
    transportationId?: number;
    purchaseOrderItemId: number;
  }>({
    initialValues: {
      partnerCode: partnerCode ?? undefined,
      code: code ?? undefined,
      direction: undefined,
      scheduledAt: "",
      itemCode: "",
      lotId: undefined,
      quantity: "",
      userCode: undefined,
      sourceLocationCode: undefined,
      targetLocationCode: undefined,
      transportationId: undefined,
      purchaseOrderItemId: 0,
    },
  });

  const { data } = useQuery({
    ...purchaseOrderItems.detail({
      purchaseOrderItemId: form.values.purchaseOrderItemId ?? 0,
      populate: ["quantities", "inputEligibleQuantity", "location"],
    }),
    enabled: !!form.values.purchaseOrderItemId,
  });

  const purchaseOrderItemsData = data?.data;

  const { data: inventory } = useQuery({
    ...inventories.find({
      query: {
        $and: [
          {
            locationCode: { $eq: form.values.sourceLocationCode },
            itemCode: { $eq: form.values.itemCode },
          },
        ],
      },
      populate: ["lot"],
    }),
    enabled: !!form.values.sourceLocationCode,
    select: (data) =>
      data?.data
        ?.filter((item) => Number(item.quantity) > 0)
        ?.map((inven) => {
          return {
            value: inven.lotId !== undefined ? String(inven.lotId) : "",
            label: `로트명 :${inven.lot?.name ?? "-"} | 유효기한 : ${inven.lot?.expiration !== null ? timeUtil(inven.lot?.expiration ?? "") : "-"} | 수량 : ${inven.quantity}`,
          };
        }),
  });

  useEffect(() => {
    if (purchaseOrderItemsData !== undefined) {
      form.setFieldValue("sourceLocationCode", purchaseOrderItemsData.sourceLocationCode);
      form.setFieldValue("targetLocationCode", purchaseOrderItemsData.targetLocationCode);
      form.setFieldValue("partnerCode", purchaseOrderItemsData.partnerCode);
      form.setFieldValue("userCode", purchaseOrderItemsData.userCode);
    }
  }, [purchaseOrderItemsData]);

  const onSubmit = async () => {
    try {
      createMutate({
        stockPlansGetRequest: {
          partnerCode: purchaseOrderItemsData?.partnerCode ?? form.values.partnerCode!,
          direction: form.values.direction!,
          code: form.values.code,
          scheduledAt: form.values.scheduledAt!,
          itemCode: form.values.itemCode!,
          lotId: Number(form.values.lotId)!,
          quantity: form.values.quantity.toString()!,
          userCode: purchaseOrderItemsData?.userCode ?? form.values.userCode!,
          sourceLocationCode:
            purchaseOrderItemsData?.sourceLocationCode ?? form.values.sourceLocationCode!,
          targetLocationCode:
            purchaseOrderItemsData?.targetLocationCode ?? form.values.targetLocationCode!,
          transportationId: form.values.transportationId!,
          purchaseOrderItemId: form.values.purchaseOrderItemId,
        },
      });
    } catch (error) {
      customAlert(
        handleErrorResponse({ error, errorContext: t("stockPlan.stockPlan") }),
        t("common.createFail", { item: t("stockPlan.stockPlan") }),
        "red",
      );
    }
  };

  return (
    <Create.Wrapper>
      <Create.Input
        label={t("stockPlan.code")}
        placeholder={t("placeholder.input", { item: t("stockPlan.code") })}
        {...form.getInputProps("code")}
      />
      <Select
        label={t("stockPlan.selectDirection")}
        placeholder={t("placeholder.select", { item: t("stockPlan.selectDirection") })}
        data={[
          {
            label: t(StockPlansGet200ResponseRowsInnerDirectionEnum.INBOUND),
            value: StockPlansGetRequestDirectionEnum.INBOUND,
          },
          {
            label: t(StockPlansGet200ResponseRowsInnerDirectionEnum.OUTBOUND),
            value: StockPlansGetRequestDirectionEnum.OUTBOUND,
          },
        ]}
        withAsterisk
        {...form.getInputProps("direction")}
      />
      <Create.DateInput
        popoverProps={{ withinPortal: true }}
        icon={<IconCalendar size="1.2rem" />}
        label={t("stockPlan.scheduledAt")}
        valueFormat="YYYY-MM-DD"
        locale="ko"
        firstDayOfWeek={0}
        {...form.getInputProps("scheduledAt")}
        withAsterisk
      />
      <ItemAutoComplete
        label={t("item.code")}
        placeholder={t("placeholder.select", { item: t("item.code") })}
        {...form.getInputProps("itemCode")}
        withAsterisk
      />
      <Checkbox
        label={"수주항목정보 가져오기"}
        checked={isChecked}
        onChange={(event) => {
          setIsChecked(event.currentTarget.checked);
        }}
      />
      <PurchaseOrderItemsAutoComplete
        label={t("purchaseOrderItems.id")}
        disabled={!isChecked}
        placeholder={t("placeholder.select", {
          item: t("purchaseOrderItems.purchaseOrderItems"),
        })}
        itemCodes={form.values.itemCode ?? ""}
        direction={form.values.direction === "INBOUND" ? "OUTBOUND" : "INBOUND"}
        onChange={(val, option) => {
          form.setFieldValue("purchaseOrderItemId", Number(val));
          form.setFieldValue("quantity", option?.availableStockPlanQuantity ?? "");
        }}
        // {...form.getInputProps("purchaseOrderItemId")}
      />
      <LocationAutoComplete
        label={t("stockPlan.sourceLocation")}
        placeholder={t("placeholder.select", { item: t("stockPlan.sourceLocation") })}
        {...form.getInputProps("sourceLocationCode")}
      />
      <LocationAutoComplete
        label={t("stockPlan.targetLocation")}
        placeholder={t("placeholder.select", { item: t("stockPlan.targetLocation") })}
        {...form.getInputProps("targetLocationCode")}
      />
      <Select
        label={t("lot.lot")}
        placeholder={t("placeholder.select", { item: t("lot.lot") })}
        disabled={inventory?.length === 0}
        data={inventory ?? []}
        clearable
        {...form.getInputProps("lotId")}
      />
      <NumberInput
        label={t("inventory.quantity")}
        placeholder={t("placeholder.input", { item: t("inventory.quantity") })}
        withAsterisk
        value={
          typeof form.values.quantity === "number"
            ? form.values.quantity
            : form.values.quantity === "" || form.values.quantity === undefined
              ? ""
              : Number(form.values.quantity)
        }
        onChange={(value) => form.setFieldValue("quantity", value.toString())}
        max={
          form.values.purchaseOrderItemId
            ? Number(purchaseOrderItemsData?.quantity) -
              Number(purchaseOrderItemsData?.sumOfStockPlanQuantity)
            : undefined
        }
      />
      <UserAutoComplete
        label={t("order.manager")}
        placeholder={t("placeholder.select", { item: t("order.manager") })}
        {...form.getInputProps("userCode")}
      />
      <PartnerAutoComplete
        label={t("partner.partner")}
        disabled={!!form.values.partnerCode}
        placeholder={t("placeholder.select", { item: t("partner.partner") })}
        {...form.getInputProps("partnerCode")}
      />
      <Create.ButtonBox>
        <Create.Button color="gray" onClick={closeModal}>
          {t("common.cancel")}
        </Create.Button>
        <Create.Button
          disabled={validateFormValues({
            pageType: "stockPlan",
            values: form.values,
          })}
          onClick={autoCompleteOnSubmit ? () => autoCompleteOnSubmit(form.values) : onSubmit}
        >
          {t("common.additional", { item: t("stockPlan.stockPlan") })}
        </Create.Button>
      </Create.ButtonBox>
    </Create.Wrapper>
  );
};
